/**
 * GPU Frustum Cull — TSL compute kernel.
 *
 * **STATUS: STUB / WIP.** The kernel body below does not implement the
 * frustum plane test — it unconditionally writes `visible=1`. It exists
 * only as scaffolding for the eventual real implementation.
 *
 * **Do not dispatch this compute node in production.** `FrustumCullSystem`
 * does its own CPU path via the spatial index and does not reach this file.
 * The export is retained for wiring-up tests and future implementation.
 *
 * `extractFrustumPlanes` is the sibling utility used by the CPU path and is
 * fully implemented.
 *
 * @internal — not part of the stable engine API.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, storage, instanceIndex, int,
} from 'three/tsl';
import { Logger } from '../../core/Logger.js';

export interface GPUCullBuffers {
  /** Float32: [cx, cy, cz, hx, hy, hz] per instance */
  boundsBuffer: THREE.StorageBufferAttribute;
  /** Uint32: [drawCount] per instance — 1=visible, 0=culled */
  visibilityBuffer: THREE.StorageBufferAttribute;
  /** Float32: 24 floats (6 planes × 4 components) */
  planesUniform: THREE.Uniform<Float32Array>;
  instanceCount: number;
}

/**
 * Create GPU cull compute node.
 * Returns a compute node that can be called per-frame with updated frustum planes.
 *
 * @deprecated The kernel body is a no-op stub that writes `visible=1` for
 *   every instance. Use CPU culling via `FrustumCullSystem` until the real
 *   TSL frustum test is implemented.
 */
export function createGPUCullCompute(
  buffers: GPUCullBuffers,
): unknown | null {
  Logger.warn(
    'GPUCull',
    'createGPUCullCompute() is a stub — kernel unconditionally marks all instances visible. Use FrustumCullSystem (CPU) instead.',
  );
  try {
    const bounds = storage(buffers.boundsBuffer, 'vec4', buffers.instanceCount);
    const visibility = storage(buffers.visibilityBuffer, 'uint', buffers.instanceCount);
    // planes uniform for GPU-side frustum test (reserved for full implementation)
    void uniform(buffers.planesUniform);

    const computeFn = Fn(() => {
      const idx = instanceIndex;

      // Read AABB center + half extents (packed as 2 vec4s)
      // Reserved for full frustum plane test
      void bounds.element(int(idx).mul(2));
      void bounds.element(int(idx).mul(2).add(1));

      // Simplified: set all visible (full GPU plane test can be added later)
      const visible = int(1);
      visibility.element(idx).assign(visible);
    });

    const computeNode = computeFn().compute(buffers.instanceCount);
    Logger.debug('GPUCull', `Compute node created for ${buffers.instanceCount} instances`);
    return computeNode;
  } catch (err) {
    Logger.warn('GPUCull', `Failed to create compute node: ${err}`);
    return null;
  }
}

/**
 * Extract frustum planes from a projection-view matrix.
 * Returns 24 floats: 6 planes × (nx, ny, nz, d).
 */
export function extractFrustumPlanes(projViewMatrix: THREE.Matrix4): Float32Array {
  const m = projViewMatrix.elements;
  const planes = new Float32Array(24);

  // Left
  planes[0] = m[3] + m[0]; planes[1] = m[7] + m[4]; planes[2] = m[11] + m[8]; planes[3] = m[15] + m[12];
  // Right
  planes[4] = m[3] - m[0]; planes[5] = m[7] - m[4]; planes[6] = m[11] - m[8]; planes[7] = m[15] - m[12];
  // Bottom
  planes[8] = m[3] + m[1]; planes[9] = m[7] + m[5]; planes[10] = m[11] + m[9]; planes[11] = m[15] + m[13];
  // Top
  planes[12] = m[3] - m[1]; planes[13] = m[7] - m[5]; planes[14] = m[11] - m[9]; planes[15] = m[15] - m[13];
  // Near
  planes[16] = m[3] + m[2]; planes[17] = m[7] + m[6]; planes[18] = m[11] + m[10]; planes[19] = m[15] + m[14];
  // Far
  planes[20] = m[3] - m[2]; planes[21] = m[7] - m[6]; planes[22] = m[11] - m[10]; planes[23] = m[15] - m[14];

  // Normalize each plane
  for (let i = 0; i < 6; i++) {
    const offset = i * 4;
    const len = Math.sqrt(planes[offset] ** 2 + planes[offset + 1] ** 2 + planes[offset + 2] ** 2);
    if (len > 0) {
      planes[offset] /= len;
      planes[offset + 1] /= len;
      planes[offset + 2] /= len;
      planes[offset + 3] /= len;
    }
  }

  return planes;
}
